/**
 * Admin Dashboard
 * Admins only - User management with role assignment
 */

import { useState, useEffect } from 'react';
import { useAdmin } from '../hooks/useAdmin';
import { useAuth } from '../hooks/useAuth';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

interface SearchResult {
  email: string;
  sub: string;
  givenName: string;
  familyName: string;
  role: 'admin' | 'instructor' | 'student';
}

export function AdminDashboard() {
  const { user: currentUser } = useAuth();
  const {
    users,
    loading,
    error,
    fetchUsers,
    deleteUser,
    searchUser,
    assignRole,
  } = useAdmin();

  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  // Assign Role dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignEmail, setAssignEmail] = useState('');
  const [foundUser, setFoundUser] = useState<SearchResult | null>(null);
  const [assignSearchState, setAssignSearchState] = useState<'idle' | 'searching' | 'found' | 'not-found'>('idle');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Fetch elevated users on mount
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Filter visible list by search term
  const filteredUsers = users.filter((user) => {
    return (
      !searchTerm ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${user.givenName} ${user.familyName}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Stats
  const adminsCount = users.filter((u) => u.role === 'admin').length;
  const instructorsCount = users.filter((u) => u.role === 'instructor').length;

  const resetAssignDialog = () => {
    setAssignEmail('');
    setFoundUser(null);
    setAssignSearchState('idle');
    setSelectedRole('');
    setIsAssigning(false);
  };

  const handleFindUser = async () => {
    if (!assignEmail.trim()) return;
    setAssignSearchState('searching');
    setFoundUser(null);
    try {
      const result = await searchUser(assignEmail.trim());
      setFoundUser(result.data);
      setSelectedRole(result.data.role);
      setAssignSearchState('found');
    } catch {
      setAssignSearchState('not-found');
    }
  };

  const handleAssignRoleSubmit = async () => {
    if (!foundUser || !selectedRole) return;
    setIsAssigning(true);
    try {
      await assignRole(foundUser.email, selectedRole);
      setSuccessMessage(`Assigned role "${selectedRole}" to ${foundUser.email}`);
      await fetchUsers();
      setAssignDialogOpen(false);
      resetAssignDialog();
    } catch {
      // error already set in hook
    } finally {
      setIsAssigning(false);
    }
  };

  const handleInlineRoleChange = async (email: string, newRole: string) => {
    setSuccessMessage('');
    try {
      await assignRole(email, newRole);
      setSuccessMessage(`Updated ${email} to ${newRole}`);
      await fetchUsers();
    } catch {
      // error already set in hook
    }
  };

  const handleDeleteClick = (email: string) => {
    setUserToDelete(email);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    try {
      await deleteUser(userToDelete);
      setSuccessMessage(`Deleted user ${userToDelete}`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (err) {
      console.error('Delete failed:', err);
      setDeleteDialogOpen(false);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'instructor':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'student':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">User Management</h2>
          <p className="text-muted-foreground mt-2">Manage users and assign roles</p>
        </div>
        <Button
          onClick={() => {
            setAssignDialogOpen(true);
            resetAssignDialog();
          }}
        >
          + Assign Role
        </Button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Search */}
      <div className="flex gap-4">
        <Input
          placeholder="Search email or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1"
        />
      </div>

      {/* User Table — shows admins + instructors only */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">
                  Loading users...
                </TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  No elevated users found
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => {
                const isCurrentUser = user.email === currentUser?.email;

                return (
                  <TableRow key={user.email}>
                    <TableCell className="font-medium">{user.email}</TableCell>
                    <TableCell>
                      {user.givenName} {user.familyName}
                    </TableCell>
                    <TableCell>
                      {isCurrentUser ? (
                        <Badge className={getRoleBadgeColor(user.role)}>
                          {user.role} (you)
                        </Badge>
                      ) : (
                        <Select
                          value={user.role}
                          onValueChange={(newRole) => handleInlineRoleChange(user.email, newRole)}
                          disabled={loading}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="student">Student</SelectItem>
                            <SelectItem value="instructor">Instructor</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!isCurrentUser && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(user.email)}
                          disabled={loading}
                        >
                          Delete
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {filteredUsers.length > 0 && (
          <div className="px-4 py-3 border-t text-sm text-muted-foreground">
            Showing {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Stats */}
      <div>
        <h3 className="text-lg font-semibold mb-4">System Statistics</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="border rounded-lg p-4">
            <div className="text-2xl font-bold">{adminsCount}</div>
            <div className="text-sm text-muted-foreground">Admins</div>
          </div>
          <div className="border rounded-lg p-4">
            <div className="text-2xl font-bold">{instructorsCount}</div>
            <div className="text-sm text-muted-foreground">Instructors</div>
          </div>
        </div>
      </div>

      {/* Assign Role Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={(open) => {
        setAssignDialogOpen(open);
        if (!open) resetAssignDialog();
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Role</DialogTitle>
            <DialogDescription>
              Find a Cognito user by email and assign them a role.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Step 1: Search */}
            <div className="flex gap-2">
              <Input
                placeholder="user@example.com"
                value={assignEmail}
                onChange={(e) => {
                  setAssignEmail(e.target.value);
                  if (assignSearchState !== 'idle') setAssignSearchState('idle');
                  setFoundUser(null);
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleFindUser()}
                disabled={assignSearchState === 'searching'}
              />
              <Button
                onClick={handleFindUser}
                disabled={!assignEmail.trim() || assignSearchState === 'searching'}
                variant="outline"
              >
                {assignSearchState === 'searching' ? 'Searching...' : 'Find User'}
              </Button>
            </div>

            {assignSearchState === 'not-found' && (
              <p className="text-sm text-red-600">
                No Cognito user found with that email address.
              </p>
            )}

            {/* Step 2: Assign (shown after user is found) */}
            {assignSearchState === 'found' && foundUser && (
              <div className="space-y-3 border rounded-lg p-3 bg-muted/30">
                <div>
                  <p className="font-medium">{foundUser.email}</p>
                  {(foundUser.givenName || foundUser.familyName) && (
                    <p className="text-sm text-muted-foreground">
                      {foundUser.givenName} {foundUser.familyName}
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground mt-1">
                    Current role: <Badge className={getRoleBadgeColor(foundUser.role)}>{foundUser.role}</Badge>
                  </p>
                </div>

                <div className="flex gap-2 items-center">
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="instructor">Instructor</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button
                    onClick={handleAssignRoleSubmit}
                    disabled={!selectedRole || isAssigning}
                  >
                    {isAssigning ? 'Assigning...' : 'Assign Role'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Delete user {userToDelete}? This will remove them from Cognito and all classes.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
